import React, { useEffect, useRef, useState, useCallback } from "react";
import styles from "./home.module.css";
import venchy from "../../images/questionnaire_doctor.png";
import { getPrintDate } from "../../utils/utils";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import SuicideRiskAlerts from "../../components/suicideRiskAlerts/suicideRiskAlerts";
import PendingTreatmentPlans from "../../components/pendingTreatmentPlans/pendingTreatmentPlans";
import CompanyInquiries from "../../components/companyInquiries/companyInquiries";
import Gated from "../../components/Gated/Gated";
import TherapistsQuestionsReports from "../../components/therapistsQuestionsReports/therapistsQuestionsReports";
import {
  adminLinks,
} from "../../utils/constants";
import { useLocation, useNavigate } from "react-router-dom";
// import stylesMain from "../../styles/index.css"
import { tWithKeyAttr as t } from "../../i18nHelper";
import i18n from "../../i18n";
import { useGetAdminMessagesMutation } from "../../services/api/utils.api";
import { IAdminMessage } from "../../services/types/utils.types";
import { useTablePermissions } from "../../hooks/useTablePermissions";
import { TABLE_IDS } from "../../utils/constants";

// Helper function to parse text and convert URLs to clickable links
const renderTextWithLinks = (text: string): React.ReactNode => {
  if (!text) return null;

  const combinedRegex = /(\[([^\]]+)\]\((https?:\/\/[^)]+|www\.[^)]+)\))|(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;

  while ((match = combinedRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }

    if (match[1]) {
      const linkText = match[2];
      const url = match[3];
      const href = url.startsWith('www.') ? `https://${url}` : url;
      result.push(
        <a
          key={keyIndex++}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {linkText}
        </a>
      );
    } else if (match[4]) {
      const url = match[4];
      const href = url.startsWith('www.') ? `https://${url}` : url;
      result.push(
        <a
          key={keyIndex++}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {url}
        </a>
      );
    }

    lastIndex = combinedRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result.length > 0 ? result : text;
};

type UpdateItem = {
  id: number;
  name: string;
  date: string;
  content: React.ReactNode[];
  image: string;
};


function HomePage() {
  const admin = useAppSelector(adminSelector);
  const companyId = admin?.companyId;
  const navigate = useNavigate();
  const location = useLocation();
  const mainSection = useRef<HTMLElement | null>(null);

  const [getMessages] = useGetAdminMessagesMutation();
  const { hasPermission, isReadOnly, isLoading: isPermissionsLoading } = useTablePermissions();
  const [updates, setUpdates] = useState<UpdateItem[]>([]);
  const [currentUpdate, setCurrentUpdate] = useState(0);
  // const HEADER_OFFSET = 100;

  useEffect(() => {
    if (!location.hash) return;
    // if not rendered yet, it will try up to 20 times in intervals of 100ms per each till rendered.
    let attempts = 0;
    const maxAttempts = 20;
    const delay = 100;

    const scrollToElement = () => {
      const el = document.querySelector(location.hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(scrollToElement, delay);
      }
    };
    scrollToElement();
  }, [location]);

  useEffect(() => {
    if (!mainSection.current) return;
    const scrollContainer = mainSection.current.closest(
      '[data-scroll-container="true"]'
    ) as HTMLElement | null;
    if (scrollContainer) {
      scrollContainer?.classList.add(styles.scroll_offset);
    }
  }, []);

  // Default fallback update
  const defaultUpdate: UpdateItem = {
    id: 1,
    name: t('VENCHI_TEAM'),
    date: getPrintDate(new Date()),
    content: [
      t('NEW_APP_LAUNCH_TITLE'),
      t('NEW_APP_LAUNCH_CONTENT_1'),
      t('NEW_APP_LAUNCH_CONTENT_2'),
      t('NEW_APP_LAUNCH_CONTENT_3'),
    ],
    image: venchy,
  };

  // Fetch admin messages (roleId = 0)
  const fetchAdminMessages = useCallback(async () => {
    if (companyId === undefined || companyId === null) {
      setUpdates([defaultUpdate]);
      return;
    }

    try {
      const res = await getMessages({ companyId, roleId: 0 });

      if ("data" in res && res.data && res.data.length > 0) {
        const activeMessages = res.data.filter((m: IAdminMessage) => m.StatusId !== 0);

        if (activeMessages.length > 0) {
          const mappedUpdates: UpdateItem[] = activeMessages.map((msg: IAdminMessage) => {
            const contentItems: React.ReactNode[] = [];
            if (msg.Title) {
              contentItems.push(msg.Title);
            }
            if (msg.Desc) {
              contentItems.push(renderTextWithLinks(msg.Desc));
            }
            return {
              id: msg.Id,
              name: msg.CreatedBySuperAdmin || !msg.CreatedUserName
                ? t('SYSTEM_ADMINISTRATOR')
                : msg.CreatedUserName,
              date: msg.UpdatedAt ? getPrintDate(new Date(msg.UpdatedAt)) : getPrintDate(new Date()),
              content: contentItems,
              image: venchy,
            };
          });
          setUpdates(mappedUpdates);
        } else {
          setUpdates([defaultUpdate]);
        }
      } else {
        setUpdates([defaultUpdate]);
      }
    } catch (error) {
      console.error("Failed to fetch admin messages:", error);
      setUpdates([defaultUpdate]);
    }
  }, [companyId, getMessages]);

  useEffect(() => {
    fetchAdminMessages();
  }, [fetchAdminMessages]);

  const handleNext = () => {
    setCurrentUpdate((prev) => (prev === updates.length - 1 ? 0 : prev + 1));
  };

  const handlePrev = () => {
    setCurrentUpdate((prev) => (prev === 0 ? updates.length - 1 : prev - 1));
  };

  const update = updates[currentUpdate] || null;

  // A dot per update turned into twenty of them once real messages arrived. Show a window that
  // travels with the current position instead, and shrink whichever end still has more behind it
  // so it reads as "there is further to go" rather than as another page.
  const DOT_WINDOW = 5;
  const dotStart = Math.min(
    Math.max(currentUpdate - Math.floor(DOT_WINDOW / 2), 0),
    Math.max(updates.length - DOT_WINDOW, 0)
  );
  const visibleDots = updates.slice(dotStart, dotStart + DOT_WINDOW).map((_, i) => {
    const index = dotStart + i;
    return {
      index,
      isEdge:
        (i === 0 && dotStart > 0) ||
        (i === DOT_WINDOW - 1 && dotStart + DOT_WINDOW < updates.length),
    };
  });

  return (
    <section dir={i18n.dir()} className={`menuSection ${styles.menu_section_override}`} ref={mainSection}>
      <ul className={`${styles.tabs}`}>
        {adminLinks.map((tab) => {

          return (
            <li
              key={tab.title}
              className={`${styles.tab}`}
              onClick={() => {
                navigate(`/${tab.value}`);
              }}
            >
              <img src={tab.img} alt={tab.title} className={styles.tabsImg} />
              <p className={styles.tabText}>{t(tab.title)}</p>
            </li>
          );
        })}
      </ul>

      <article className={styles.home__section} data-testid="home-section">
        <h4 className={styles.home__sectionTitle} id="updates">
          {t('UPDATES')}</h4>

        {update && (
          <div className={styles.notifications}>
            <div className={styles.notifications__venchy}>
              <img
                src={update.image}
                className={styles.notifications__img}
                alt={t('IMAGE_OF_VENUS')}
              />
              <div>
                <p className={styles.notifications__name}>{update.name}</p>
                <p className={styles.notifications__date}>
                  {update.date}
                </p>
              </div>
            </div>
            <div className={styles.notifications__content}>
              {update.content.map((text, idx) => (
                <p className={styles.text} key={idx}>
                  {text}
                </p>
              ))}
            </div>
            {updates.length > 1 && (
              <button
                onClick={handleNext}
                aria-label="Next"
                className={styles.nextBtn}
              />
            )}
            {updates.length > 1 && (
              <button
                onClick={handlePrev}
                aria-label="Previous"
                className={styles.prevBtn}
              />
            )}
          </div>
        )}
      </article>
      <ul className={styles.notifications__dots}>
        {visibleDots.map(({ index, isEdge }) => (
          <li
            key={index}
            className={`${styles.notifications__dot} ${index === currentUpdate ? styles.notifications__dot_active : ""
              } ${isEdge ? styles.notifications__dot_edge : ""}`}
            onClick={() => setCurrentUpdate(index)}
          />
        ))}
      </ul>
      {!admin.fakeCompanyId && (
        <>
          <article className={styles.home__alert_section} id="notifications">
            <SuicideRiskAlerts />
          </article>
          {!isPermissionsLoading && (hasPermission(TABLE_IDS.NEW_INQUIRIES) || hasPermission(TABLE_IDS.MY_INQUIRIES)) && (
            <Gated feature="ONLINE_INTAKE">
              <CompanyInquiries
                readOnly={isReadOnly(TABLE_IDS.NEW_INQUIRIES)}
                showNewInquiries={hasPermission(TABLE_IDS.NEW_INQUIRIES)}
                showMyInquiries={hasPermission(TABLE_IDS.MY_INQUIRIES)}
                myInquiriesReadOnly={isReadOnly(TABLE_IDS.MY_INQUIRIES)}
              />
            </Gated>
          )}
          {!isPermissionsLoading && hasPermission(TABLE_IDS.TREATMENT_PLANS) && (
            <div id="pendingTreatmentPlans">
              <PendingTreatmentPlans readOnly={isReadOnly(TABLE_IDS.TREATMENT_PLANS)} /> </div>
          )}
          <TherapistsQuestionsReports />
        </>
      )}
    </section>
  );
}

export default HomePage;
